import { useEffect, useState, FormEvent } from 'react'
import { apiFetch } from '@/lib/api'

interface Proposal {
  id: string
  price: number
  message: string | null
  status: string
  createdAt: string
  cf: { id: string; name: string; logoUrl: string | null; isQualiopi: boolean; qualiopiDoc: string | null }
}

interface TrainingRequest {
  id: string
  topic: string
  description: string
  targetCount: number
  preferredDate: string | null
  budget: number | null
  location: string | null
  isRemote: boolean
  requiresQualiopi: boolean
  status: string
  createdAt: string
  _count?: { proposals: number }
  proposals?: Proposal[]
}

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Ouverte',
  ASSIGNED: 'Attribuée',
  COMPLETED: 'Terminée',
  CANCELLED: 'Annulée',
}

const STATUS_COLOR: Record<string, string> = {
  OPEN: 'bg-accent/10 border-accent/25 text-accent',
  ASSIGNED: 'bg-success/10 border-success/25 text-success',
  COMPLETED: 'bg-white/[0.08] border-white/[0.12] text-text-muted',
  CANCELLED: 'bg-danger/10 border-danger/25 text-danger',
}

function formatPrice(cents: number) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100)
}

export default function FormationsPage() {
  const [requests, setRequests] = useState<TrainingRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<TrainingRequest | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')
  const [acceptingId, setAcceptingId] = useState<string | null>(null)

  useEffect(() => {
    apiFetch<TrainingRequest[]>('/training-requests/company')
      .then(setRequests)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  async function openDetail(id: string) {
    const detail = await apiFetch<TrainingRequest>(`/training-requests/company/${id}`)
    setSelected(detail)
  }

  async function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setFormError('')
    setSubmitting(true)
    const fd = new FormData(e.currentTarget)
    const budgetRaw = fd.get('budget') as string
    const dto = {
      topic: fd.get('topic') as string,
      description: fd.get('description') as string,
      targetCount: Number(fd.get('targetCount')) || 1,
      preferredDate: fd.get('preferredDate') as string || undefined,
      budget: budgetRaw ? Math.round(parseFloat(budgetRaw) * 100) : undefined,
      location: fd.get('location') as string || undefined,
      isRemote: fd.get('isRemote') === 'on',
      requiresQualiopi: fd.get('requiresQualiopi') === 'on',
    }
    try {
      const created = await apiFetch<TrainingRequest>('/training-requests/company', {
        method: 'POST',
        body: JSON.stringify(dto),
      })
      setRequests((prev) => [{ ...created, _count: { proposals: 0 } }, ...prev])
      setShowForm(false)
      ;(e.target as HTMLFormElement).reset()
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Erreur lors de la création')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleAccept(requestId: string, proposalId: string) {
    setAcceptingId(proposalId)
    try {
      await apiFetch(`/training-requests/company/${requestId}/proposals/${proposalId}/accept`, { method: 'PATCH' })
      const updated = await apiFetch<TrainingRequest>(`/training-requests/company/${requestId}`)
      setSelected(updated)
      setRequests((prev) => prev.map((r) => r.id === requestId ? { ...r, status: 'ASSIGNED' } : r))
    } catch { /* silencieux */ }
    finally { setAcceptingId(null) }
  }

  async function handleCancel(id: string) {
    await apiFetch(`/training-requests/company/${id}/cancel`, { method: 'PATCH' })
    setRequests((prev) => prev.map((r) => r.id === id ? { ...r, status: 'CANCELLED' } : r))
    if (selected?.id === id) setSelected((s) => s ? { ...s, status: 'CANCELLED' } : s)
  }

  if (loading) return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => <div key={i} className="h-24 glass-card rounded-xl animate-pulse" />)}
    </div>
  )

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Demandes de formation</h1>
          <p className="text-text-secondary text-sm mt-1">Publiez vos besoins — les centres de formation vous répondent.</p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="glass-btn-accent px-4 py-2 text-sm rounded-xl flex items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Nouvelle demande
        </button>
      </div>

      {/* Formulaire création */}
      {showForm && (
        <div className="glass-card p-6 mb-6">
          <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wider text-[11px] opacity-60 mb-5">Nouvelle demande de formation</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="glass-label">Sujet / thème de la formation <span className="text-danger">*</span></label>
              <input name="topic" required className="glass-input" placeholder="ex. Sécurité incendie SSIAP, Premiers secours PSC1…" />
            </div>
            <div>
              <label className="glass-label">Description du besoin <span className="text-danger">*</span></label>
              <textarea name="description" required rows={3} className="glass-input resize-none" placeholder="Décrivez vos objectifs, le public ciblé, les contraintes particulières…" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="glass-label">Nombre de participants</label>
                <input name="targetCount" type="number" min={1} defaultValue={1} className="glass-input" />
              </div>
              <div>
                <label className="glass-label">Budget indicatif (€)</label>
                <input name="budget" type="number" min={0} step="0.01" className="glass-input" placeholder="ex. 1500" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="glass-label">Date souhaitée</label>
                <input name="preferredDate" type="date" className="glass-input" />
              </div>
              <div>
                <label className="glass-label">Lieu</label>
                <input name="location" className="glass-input" placeholder="Ville ou adresse" />
              </div>
            </div>

            {/* Checkboxes */}
            <div className="space-y-3 pt-1">
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="isRemote"
                  className="w-4 h-4 rounded accent-[#6E7FFF] cursor-pointer"
                />
                <span className="text-text-secondary text-sm group-hover:text-text-primary transition-colors">Formation à distance possible</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="requiresQualiopi"
                  className="w-4 h-4 mt-0.5 rounded accent-[#6E7FFF] cursor-pointer"
                />
                <span className="text-sm group-hover:text-text-primary transition-colors">
                  <span className="text-text-secondary">Centre de formation </span>
                  <span className="font-semibold text-[#F59E0B]">certifié Qualiopi obligatoire</span>
                  <span className="block text-text-muted text-xs mt-0.5">Requis pour les formations financées (OPCO, CPF, Pôle emploi…)</span>
                </span>
              </label>
            </div>

            {formError && <p className="text-danger text-sm">{formError}</p>}
            <div className="flex gap-3 pt-1">
              <button type="submit" disabled={submitting} className="glass-btn-accent px-5 py-2 text-sm rounded-xl">
                {submitting ? 'Envoi…' : 'Publier la demande'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="glass-btn px-5 py-2 text-sm rounded-xl">
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Détail d'une demande */}
      {selected && (
        <div className="glass-card p-6 mb-6 border border-accent/20">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-base font-semibold text-text-primary">{selected.topic}</h2>
              <p className="text-text-muted text-xs mt-0.5">{new Date(selected.createdAt).toLocaleDateString('fr-FR')}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLOR[selected.status]}`}>
                {STATUS_LABEL[selected.status]}
              </span>
              <button onClick={() => setSelected(null)} className="p-1.5 rounded-lg hover:bg-white/[0.08] text-text-muted transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>

          <p className="text-text-secondary text-sm mb-4 leading-relaxed">{selected.description}</p>

          <div className="flex flex-wrap gap-2 mb-5">
            <span className="px-2.5 py-1 rounded-lg bg-white/[0.05] border border-white/[0.08] text-text-secondary text-xs">{selected.targetCount} participant{selected.targetCount > 1 ? 's' : ''}</span>
            {selected.budget && <span className="px-2.5 py-1 rounded-lg bg-white/[0.05] border border-white/[0.08] text-text-secondary text-xs">Budget : {formatPrice(selected.budget)}</span>}
            {selected.preferredDate && <span className="px-2.5 py-1 rounded-lg bg-white/[0.05] border border-white/[0.08] text-text-secondary text-xs">Souhaitée : {new Date(selected.preferredDate).toLocaleDateString('fr-FR')}</span>}
            {selected.location && <span className="px-2.5 py-1 rounded-lg bg-white/[0.05] border border-white/[0.08] text-text-secondary text-xs">{selected.location}</span>}
            {selected.isRemote && <span className="px-2.5 py-1 rounded-lg bg-accent/10 border border-accent/20 text-accent text-xs">À distance ✓</span>}
            {selected.requiresQualiopi && <span className="px-2.5 py-1 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/25 text-[#F59E0B] text-xs">Qualiopi requis</span>}
          </div>

          {/* Propositions */}
          <div>
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
              Propositions ({selected.proposals?.length ?? 0})
            </h3>
            {!selected.proposals?.length && (
              <p className="text-text-muted text-sm">Aucune proposition reçue pour l'instant.</p>
            )}
            <div className="space-y-3">
              {selected.proposals?.map((p) => (
                <div
                  key={p.id}
                  className={`p-4 rounded-xl border transition-all ${p.status === 'ACCEPTED' ? 'bg-success/[0.06] border-success/25' : 'bg-white/[0.04] border-white/[0.08]'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-text-primary text-sm font-semibold">{p.cf.name}</p>
                        {p.cf.isQualiopi && (
                          <span className="px-2 py-0.5 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/25 text-[#F59E0B] text-[10px] font-semibold">Qualiopi</span>
                        )}
                        {p.status === 'ACCEPTED' && <span className="px-2 py-0.5 rounded-full bg-success/10 border border-success/25 text-success text-[10px] font-semibold">Acceptée</span>}
                        {p.status === 'DECLINED' && <span className="px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/[0.10] text-text-muted text-[10px]">Refusée</span>}
                      </div>
                      {p.message && <p className="text-text-secondary text-xs leading-relaxed">{p.message}</p>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-text-primary font-semibold">{formatPrice(p.price)}</p>
                      {selected.status === 'OPEN' && p.status === 'PENDING' && (
                        <button
                          onClick={() => handleAccept(selected.id, p.id)}
                          disabled={acceptingId === p.id}
                          className="mt-2 glass-btn-accent px-3 py-1 text-xs rounded-lg"
                        >
                          {acceptingId === p.id ? '…' : 'Accepter'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selected.status === 'OPEN' && (
            <div className="mt-4 pt-4 border-t border-white/[0.08]">
              <button
                onClick={() => handleCancel(selected.id)}
                className="glass-btn px-4 py-1.5 text-xs rounded-lg text-danger border-danger/20 hover:bg-danger/10"
              >
                Annuler la demande
              </button>
            </div>
          )}
        </div>
      )}

      {/* Liste */}
      {requests.length === 0 && !showForm && (
        <div className="glass-card p-10 text-center">
          <p className="text-4xl mb-3">🎓</p>
          <p className="text-text-primary font-medium mb-1">Aucune demande de formation</p>
          <p className="text-text-secondary text-sm">Publiez votre premier besoin pour recevoir des propositions de centres de formation.</p>
        </div>
      )}

      <div className="space-y-3">
        {requests.map((r) => (
          <button
            key={r.id}
            onClick={() => openDetail(r.id)}
            className={`w-full text-left glass-card p-5 rounded-xl hover:bg-white/[0.06] transition-all border ${selected?.id === r.id ? 'border-accent/30' : 'border-transparent'}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-text-primary font-medium truncate">{r.topic}</p>
                  {r.requiresQualiopi && (
                    <span className="px-2 py-0.5 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/25 text-[#F59E0B] text-[10px] font-semibold flex-shrink-0">Qualiopi</span>
                  )}
                </div>
                <p className="text-text-muted text-xs">
                  {r.targetCount} participant{r.targetCount > 1 ? 's' : ''}
                  {r.budget ? ` · Budget ${formatPrice(r.budget)}` : ''}
                  {r.preferredDate ? ` · ${new Date(r.preferredDate).toLocaleDateString('fr-FR')}` : ''}
                  {' · '}
                  {new Date(r.createdAt).toLocaleDateString('fr-FR')}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {(r._count?.proposals ?? 0) > 0 && (
                  <span className="px-2.5 py-1 rounded-full bg-accent/10 border border-accent/25 text-accent text-xs font-semibold">
                    {r._count!.proposals} prop.
                  </span>
                )}
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLOR[r.status]}`}>
                  {STATUS_LABEL[r.status]}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
